import React, { useState, useCallback } from "react";
import {
    CommentItemWrapper,
    CommentHeader,
    CommentHeaderData,
    CommentItemUsername,
    CommentItemBody,
    ShowMoreBtn,
} from "./PollitemComments.styled";
import { StyledTime } from "../PollItemHeader/PollItemHeader.styled";
import MoreIcon from "@mui/icons-material/MoreHoriz";
import moment from "moment";
import { IComment } from "@Interfaces/Entities/Poll";
import UserAvatar from "../UserAvatar";
import UrlifyLink from "@Components/UrlLink";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { useCommentItem } from "./useCommentItem";
import { useTranslation } from "react-i18next";

const COMMENT_MAX_LETTERS = 250;

const ShowMoreButton = ({
    isLongBody,
    showMoreContent,
    toggleShowMore,
}: {
    isLongBody: boolean;
    showMoreContent: boolean;
    toggleShowMore: () => void;
}) =>
    isLongBody && !showMoreContent ? (
        <ShowMoreBtn onClick={toggleShowMore} aria-expanded={showMoreContent}>
            Show more
        </ShowMoreBtn>
    ) : null;

const CommentItem: React.FC<{
    commentData: IComment;
    channelId: string;
    activeComment: any;
    setActiveComment: any;
}> = ({ commentData, channelId, activeComment, setActiveComment }) => {
    const { i18n } = useTranslation();
    const [showMore, setShowMore] = useState<boolean>(false);
    const {
        anonymous_author,
        _id: commentId,
        poll_id: pollId,
        body: commentBody,
        published_date: publishedDate,
    } = commentData;

    const { handelOpenMenu, handleClose, handleDeleteComment, anchorEl } =
        useCommentItem({
            channelId,
            pollId,
            commentId,
            setActiveComment,
        });

    // Memoized function for toggling "Show More" state
    const toggleShowMore = useCallback(() => setShowMore(true), []);

    const isShowMoreCase = commentBody.length > COMMENT_MAX_LETTERS;

    const commentBodyContent = isShowMoreCase
        ? showMore
            ? `${commentBody} `
            : `${commentBody.substring(0, COMMENT_MAX_LETTERS)} `
        : commentBody;

    return (
        <>
            <CommentItemWrapper>
                <CommentHeader>
                    <UserAvatar
                        username={anonymous_author || "guest"}
                        size="small"
                    />
                    <CommentHeaderData>
                        <CommentItemUsername>
                            {anonymous_author} &nbsp;
                            <StyledTime>
                                {moment(publishedDate)
                                    .locale(i18n.language)
                                    .fromNow()}
                            </StyledTime>
                        </CommentItemUsername>
                        <IconButton
                            aria-label="More"
                            aria-controls="comment-more-menu"
                            aria-haspopup="true"
                            onClick={handelOpenMenu}
                            size="small"
                        >
                            <MoreIcon />
                        </IconButton>
                        <Menu
                            id="comment-more-menu"
                            anchorEl={anchorEl}
                            keepMounted
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                        >
                            <MenuItem
                                onClick={handleDeleteComment}
                                aria-label="Delete comment"
                            >
                                Delete
                            </MenuItem>
                        </Menu>
                    </CommentHeaderData>
                </CommentHeader>
                <CommentItemBody>
                    <UrlifyLink text={commentBodyContent} />
                    <ShowMoreButton
                        isLongBody={isShowMoreCase}
                        showMoreContent={showMore}
                        toggleShowMore={toggleShowMore}
                    />
                </CommentItemBody>
            </CommentItemWrapper>
        </>
    );
};

export default CommentItem;
